/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Select, Button, DatePicker } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Cell, LabelList,
} from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const NAVY      = '#1a3a5c';
const toN       = v => Number(v || 0);
const cpkColor  = v => v > 8 ? '#f5222d' : v > 4 ? '#fa8c16' : '#52c41a';

function KPI({ label, value, color=NAVY, sub }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 18px', borderTop:`3px solid ${color}`, flex:1, minWidth:120 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:20, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
        </div>
    );
}
function ChartCard({ title, children, height=280 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

export default function FleetAnalyticsCostPerKm() {
    // TCO has: total_cost, total_km, fuel_cost, repair_cost, vehicle_type, facility, region
    // Fuel report has: registration_no, vehicle_type, facility, month, total_litres, total_cost, km_driven, litres_per_100km
    const [tco,     setTco]     = useState([]);
    const [fuel,    setFuel]    = useState([]);
    const [loading, setLoading] = useState(true);
    const [region,    setRegion]   = useState('');
    const [programme, setProgramme]= useState('');
    const [facility,  setFacility] = useState('');
    const [vehType,   setVehType]  = useState('');
    const [dateFrom,  setDateFrom] = useState('');
    const [dateTo,    setDateTo]   = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2] = await Promise.all([
                FleetService.reportTCO(),
                FleetService.reportFuel(),
            ]);
            if (Number(r1?.data?.code) === 200) setTco(r1.data.result   || []);
            if (Number(r2?.data?.code) === 200) setFuel(r2.data.result  || []);
        } catch (e) { message.error('Failed to load data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const regions = useMemo(() => [...new Set(tco.map(r=>r.region).filter(Boolean))], [tco]);

    // Per-vehicle cost/km (TCO provides total_km and total_cost)
    const vehicles = useMemo(() => {
        let d = tco.filter(r => toN(r.total_km) > 0);
        if (region) d = d.filter(r => r.region === region);
        return d.map(r => ({
            reg:       r.registration_no,
            type:      r.vehicle_type,
            region:    r.region,
            facility:  r.facility,
            totalCost: toN(r.total_cost),
            totalKm:   toN(r.total_km),
            fuelCost:  toN(r.fuel_cost),
            repairCost:toN(r.repair_cost),
            cpk:       +(toN(r.total_cost)/toN(r.total_km)).toFixed(2),
            fuelKm:    +(toN(r.fuel_cost)/toN(r.total_km)).toFixed(2),
        })).sort((a,b) => b.cpk - a.cpk);
    }, [tco, region]);

    // Monthly fuel trend (from fuel report — grouped by month)
    const monthlyFuel = useMemo(() => {
        const m = {};
        fuel.forEach(r => {
            const mo = r.month || 'Unknown';
            if (!m[mo]) m[mo] = { month:mo, litres:0, cost:0, km:0 };
            m[mo].litres += toN(r.total_litres);
            m[mo].cost   += toN(r.total_cost);
            m[mo].km     += toN(r.km_driven);
        });
        return Object.values(m).sort((a,b)=>a.month>b.month?1:-1).slice(-12)
            .map(m => ({ ...m, lPer100: m.km>0 ? +(m.litres/m.km*100).toFixed(1) : 0 }));
    }, [fuel]);

    // By vehicle type
    const byType = useMemo(() => {
        const m = {};
        vehicles.forEach(v => {
            const k = v.type || 'Other';
            if (!m[k]) m[k] = { name:k, cost:0, km:0 };
            m[k].cost += v.totalCost;
            m[k].km   += v.totalKm;
        });
        return Object.values(m).map(t=>({ ...t, cpk: t.km>0 ? +(t.cost/t.km).toFixed(2) : 0 }))
            .sort((a,b)=>b.cpk-a.cpk);
    }, [vehicles]);

    const avgCpk     = vehicles.length ? (vehicles.reduce((s,v)=>s+v.cpk,0)/vehicles.length).toFixed(2) : 0;
    const totalKm    = vehicles.reduce((s,v)=>s+v.totalKm,0);
    const highCount  = vehicles.filter(v=>v.cpk>8).length;

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(vehicles.map(v=>({
            'Reg No':v.reg,'Type':v.type,'Region':v.region,'Facility':v.facility,
            'Total KM':v.totalKm,'Total Cost (E)':v.totalCost,
            'Cost/KM (E)':v.cpk,'Fuel/KM (E)':v.fuelKm,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb,ws,'Cost per KM');
        saveAs(new Blob([XLSX.write(wb,{bookType:'xlsx',type:'array'})]),
            `Fleet_CostPerKm_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Cost per KM Analysis</h4>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Running cost efficiency per kilometre · fuel consumption trends · type comparison
                    </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <Select allowClear placeholder="All Regions" size="small" style={{ width:130 }}
                        value={region||undefined} onChange={v=>setRegion(v||'')}>
                        {regions.map(r=><Select.Option key={r} value={r}>{r}</Select.Option>)}
                    </Select>
                    <Button size="small" icon={<ReloadOutlined/>} onClick={load}/>
                    <Button size="small" type="primary" icon={<DownloadOutlined/>}
                        onClick={exportXlsx} disabled={!vehicles.length}>Export</Button>
                </div>
            </div>

            <Spin spinning={loading}>
                <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
                    <KPI label="Vehicles w/ KM data"     value={vehicles.length}            color={NAVY}/>
                    <KPI label="Total KM Driven"          value={`${totalKm.toLocaleString()} km`} color="#2e86c1"/>
                    <KPI label="Fleet Avg Cost/KM"        value={`E ${avgCpk}`}              color={cpkColor(Number(avgCpk))} sub="all vehicles"/>
                    <KPI label="High Cost (>E8/km)"       value={highCount}                  color="#f5222d" sub="needs attention"/>
                </div>

                <ChartCard title="Cost per KM by Vehicle — top 20 (E/km)" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={vehicles.slice(0,20).map(v=>({name:v.reg||'—',cpk:v.cpk,fuel:v.fuelKm}))}
                            layout="vertical" margin={{top:0,right:65,left:85,bottom:0}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" horizontal={false}/>
                            <XAxis type="number" tick={{fontSize:10}} tickFormatter={v=>`E${v}`}/>
                            <YAxis type="category" dataKey="name" tick={{fontSize:10}} width={80}/>
                            <Tooltip formatter={(v,n)=>[`E ${v}`, n==='cpk'?'Total/km':'Fuel/km']}/>
                            <Legend wrapperStyle={{fontSize:10}}/>
                            <Bar dataKey="fuel" name="Fuel/km"  fill="#fa8c16" stackId="a"/>
                            <Bar dataKey="cpk"  name="Total/km" fill={NAVY}    stackId="a" radius={[0,3,3,0]}>
                                <LabelList dataKey="cpk" position="right" style={{fontSize:9}} formatter={v=>`E${v}`}/>
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1.5fr', gap:14 }}>
                    <ChartCard title="Avg Cost/KM by Vehicle Type" height={260}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={byType} margin={{top:5,right:40,left:5,bottom:40}}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5"/>
                                <XAxis dataKey="name" tick={{fontSize:9}} angle={-30} textAnchor="end" height={50}/>
                                <YAxis tick={{fontSize:10}} tickFormatter={v=>`E${v}`}/>
                                <Tooltip formatter={v=>[`E ${v}/km`,'Cost/KM']}/>
                                <Bar dataKey="cpk" name="E/km" radius={[4,4,0,0]}>
                                    {byType.map((t,i)=><Cell key={i} fill={cpkColor(t.cpk)}/>)}
                                    <LabelList dataKey="cpk" position="top" style={{fontSize:9}} formatter={v=>`E${v}`}/>
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    <ChartCard title="Monthly Fuel Consumption Trend (L/100km)" height={260}>
                        {monthlyFuel.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={monthlyFuel} margin={{top:5,right:20,left:5,bottom:5}}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5"/>
                                    <XAxis dataKey="month" tick={{fontSize:10}}/>
                                    <YAxis yAxisId="l" tick={{fontSize:10}}/>
                                    <YAxis yAxisId="c" orientation="right" tick={{fontSize:10}}
                                        tickFormatter={v=>`E${(v/1000).toFixed(0)}k`}/>
                                    <Tooltip formatter={(v,n)=>[
                                        n==='lPer100'?`${v} L/100km`:
                                        n==='litres' ?`${toN(v).toFixed(0)} L`:
                                        `E ${toN(v).toLocaleString()}`, n
                                    ]}/>
                                    <Legend wrapperStyle={{fontSize:10}}/>
                                    <Line yAxisId="l" type="monotone" dataKey="lPer100" name="L/100km"   stroke={NAVY}     strokeWidth={2} dot={{r:3}}/>
                                    <Line yAxisId="c" type="monotone" dataKey="cost"    name="Fuel Cost" stroke="#fa8c16" strokeWidth={2} dot={false} strokeDasharray="5 3"/>
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <div style={{display:'flex',alignItems:'center',justifyContent:'center',
                                height:'100%',color:'#aaa',flexDirection:'column',gap:8}}>
                                <div style={{fontSize:28}}>⛽</div>
                                <div style={{fontSize:12}}>No fuel log data yet</div>
                            </div>
                        )}
                    </ChartCard>
                </div>

                {!loading && vehicles.length === 0 && (
                    <div style={{textAlign:'center',padding:60,color:'#aaa',
                        border:'2px dashed #e8e8e8',borderRadius:8}}>
                        <div style={{fontSize:32,marginBottom:12}}>🛣️</div>
                        <div style={{fontWeight:600,marginBottom:4}}>No KM data</div>
                        <div style={{fontSize:12}}>Log trips with odometer readings to see cost per KM.</div>
                    </div>
                )}
            </Spin>
        </div>
    );
}